import { Slot } from "@radix-ui/react-slot";
import { type ReactNode } from "react";

import { cn } from "@trip/utils";

export function SunsetText({ children }: { children: ReactNode }) {
  return (
    <span className="bg-linear-to-r from-sunset-left to-sunset-right bg-clip-text text-transparent">
      {children}
    </span>
  );
}

export function SunsetCaption({
  className,
  children,
  asChild = false,
}: {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const Comp = asChild ? Slot : "div";
  return (
    <Comp className={cn("text-xs font-medium", className)}>
      <SunsetText>{children}</SunsetText>
    </Comp>
  );
}

export function XXL({
  className,
  children,
  asChild = false,
}: {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const Comp = asChild ? Slot : "h1";
  return (
    <Comp
      className={cn(
        "font-display text-3xl font-semibold md:text-4xl lg:text-5xl xl:text-6xl",
        className,
      )}
    >
      {children}
    </Comp>
  );
}

export function XL({
  className,
  children,
  asChild = false,
}: {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const Comp = asChild ? Slot : "h1";
  return (
    <Comp
      className={cn(
        "font-display text-2xl font-semibold md:text-3xl lg:text-4xl xl:text-5xl",
        className,
      )}
    >
      {children}
    </Comp>
  );
}

export function H2({
  className,
  children,
  asChild = false,
}: {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const Comp = asChild ? Slot : "h2";
  return (
    <Comp
      className={cn(
        "font-display text-2xl font-semibold sm:text-3xl",
        className,
      )}
    >
      {children}
    </Comp>
  );
}

export function H3({
  className,
  children,
  asChild = false,
}: {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const Comp = asChild ? Slot : "h3";
  return (
    <Comp
      className={cn(
        "font-display text-xl font-semibold sm:text-2xl",
        className,
      )}
    >
      {children}
    </Comp>
  );
}

export function H4({
  className,
  children,
  asChild = false,
}: {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const Comp = asChild ? Slot : "h4";
  return (
    <Comp className={cn("font-display font-semibold sm:text-lg", className)}>
      {children}
    </Comp>
  );
}

export function Large({
  className,
  children,
  asChild = false,
}: {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const Comp = asChild ? Slot : "span";
  return (
    <Comp
      className={cn("font-display text-lg font-semibold sm:text-xl", className)}
    >
      {children}
    </Comp>
  );
}

export function HR({ className }: { className?: string }) {
  return <hr className={cn("h-px w-full bg-neutral-900/[0.08]", className)} />;
}

export function Label({
  className,
  children,
  asChild = false,
}: {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const Comp = asChild ? Slot : "label";
  return (
    <Comp className={cn("text-xs font-light text-neutral-500", className)}>
      {children}
    </Comp>
  );
}

export function TinyLink({
  className,
  children,
  asChild = false,
}: {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const Comp = asChild ? Slot : "a";
  return (
    <Comp
      className={cn(
        "text-prune-500 decoration-prune-500/50 hover:decoration-prune-500 text-xs underline",
        className,
      )}
    >
      {children}
    </Comp>
  );
}
